import axios from '../axiosConfig';
import { Appendix, AppendixDetail, AppendixSummary, CreateAppendixPayload } from '../types/codeIndex';

const DEFAULT_APPENDIX_PAGE_SIZE = 200;

interface AppendixSearchResponse {
  results: Appendix[];
  totalCount: number;
  totalResults: number;
}

interface AppendixSearchParams {
  Title?: string;
  Description?: string;
  EntityId?: string;
  ParentEntityId?: string;
  TextSearch?: string;
  Page?: number;
  PageSize?: number;
  Skip?: number;
  IncludeDeleted?: boolean;
  OrderByField?: string;
  Descending?: boolean;
}

function normalizeAppendix(raw: any): Appendix {
  return {
    id: raw?.id || null,
    title: raw?.title || null,
    description: raw?.description || null,
    content: raw?.content || null,
    attachment: raw?.attachment || null,
    entityId: raw?.entityId || raw?.entity_id || null,
    parentEntityId: raw?.parentEntityId || raw?.parent_entity_id || null,
    createdAt: raw?.createdAt || raw?.created_at || null,
    updatedAt: raw?.updatedAt || raw?.updated_at || null,
  };
}

function getAppendixTimeValue(value: string | null | undefined): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function compareAppendicesByRecency(a: Appendix, b: Appendix): number {
  const aTime = getAppendixTimeValue(a.updatedAt) || getAppendixTimeValue(a.createdAt);
  const bTime = getAppendixTimeValue(b.updatedAt) || getAppendixTimeValue(b.createdAt);

  if (aTime !== bTime) {
    return bTime - aTime;
  }

  return String(b.id || '').localeCompare(String(a.id || ''));
}

export function sortAppendicesByRecency(appendices: Appendix[]): Appendix[] {
  return [...appendices].sort(compareAppendicesByRecency);
}

export function pickActiveAppendix(appendices: Appendix[]): Appendix | null {
  const [latest] = sortAppendicesByRecency(appendices);
  return latest || null;
}

export function buildLatestAppendixIndex(appendices: Appendix[]): Record<string, Appendix> {
  const grouped = new Map<string, Appendix[]>();

  appendices.forEach((appendix) => {
    const entityId = appendix.entityId ? String(appendix.entityId).trim() : '';
    if (!entityId) return;

    const existing = grouped.get(entityId);
    if (existing) {
      existing.push(appendix);
    } else {
      grouped.set(entityId, [appendix]);
    }
  });

  const latestByEntityId: Record<string, Appendix> = {};

  grouped.forEach((items, entityId) => {
    const latest = pickActiveAppendix(items);
    if (latest) {
      latestByEntityId[entityId] = latest;
    }
  });

  return latestByEntityId;
}

export async function fetchAppendices(params: AppendixSearchParams = {}): Promise<AppendixSearchResponse> {
  const query: Record<string, any> = {};

  if (params.Title != null) query.Title = params.Title;
  if (params.Description != null) query.Description = params.Description;
  if (params.EntityId) query.EntityId = params.EntityId;
  if (params.ParentEntityId) query.ParentEntityId = params.ParentEntityId;
  if (params.TextSearch != null) query.TextSearch = params.TextSearch;
  if (params.Page != null) query.Page = params.Page;
  if (params.PageSize != null) query.PageSize = params.PageSize;
  if (params.Skip != null) query.Skip = params.Skip;
  if (params.IncludeDeleted != null) query.IncludeDeleted = params.IncludeDeleted;
  if (params.OrderByField != null) query.OrderByField = params.OrderByField;
  if (params.Descending != null) query.Descending = params.Descending;

  const { data } = await axios.get('/Appendix', { params: query });
  const results = Array.isArray((data as any)?.results)
    ? ((data as any).results as any[]).map(normalizeAppendix)
    : [];

  return {
    results,
    totalCount: Number((data as any)?.totalCount ?? results.length),
    totalResults: Number((data as any)?.totalResults ?? results.length),
  };
}

export async function fetchAppendicesByEntityId(entityId: string): Promise<Appendix[]> {
  if (!entityId) return [];

  const response = await fetchAppendices({
    EntityId: entityId,
    Page: 1,
    PageSize: DEFAULT_APPENDIX_PAGE_SIZE,
    Descending: true,
  });

  return sortAppendicesByRecency(response.results);
}

export async function fetchAppendicesByParentEntityId(parentEntityId: string): Promise<Appendix[]> {
  if (!parentEntityId) return [];

  const collected: Appendix[] = [];
  let page = 1;
  let totalResults = 0;

  while (page === 1 || (page - 1) * DEFAULT_APPENDIX_PAGE_SIZE < totalResults) {
    const response = await fetchAppendices({
      ParentEntityId: parentEntityId,
      Page: page,
      PageSize: DEFAULT_APPENDIX_PAGE_SIZE,
      Descending: true,
    });

    totalResults = response.totalResults;
    collected.push(...response.results);

    if (response.results.length < DEFAULT_APPENDIX_PAGE_SIZE) {
      break;
    }

    page += 1;
  }

  return sortAppendicesByRecency(collected);
}

function normalizeAppendixSummary(raw: any): AppendixSummary {
  return {
    id: String(raw?.id || ''),
    key: typeof raw?.key === 'string' && raw.key.trim() ? raw.key.trim() : null,
    title: raw?.title || '',
    description: raw?.description || '',
    letter: raw?.letter || '',
    columnCount: Number(raw?.columnCount ?? 0),
    rowCount: Number(raw?.rowCount ?? 0),
    referenceCount: Number(raw?.referenceCount ?? 0),
    createdAt: raw?.createdAt || null,
    updatedAt: raw?.updatedAt || null,
  };
}

export async function fetchCodeIndexAppendices(
  codeIndexId: string,
  options: { projectId?: string; search?: string } = {}
): Promise<AppendixSummary[]> {
  if (!codeIndexId) return [];

  const query: Record<string, any> = {};
  if (options.projectId) query.projectId = options.projectId;
  if (options.search?.trim()) query.search = options.search.trim();

  const { data } = await axios.get(`/Appendix/codeindex/${encodeURIComponent(codeIndexId)}`, { params: query });
  return Array.isArray(data) ? data.map(normalizeAppendixSummary) : [];
}

export async function createCodeIndexAppendix(
  codeIndexId: string,
  payload: CreateAppendixPayload,
  options: { projectId?: string } = {}
): Promise<AppendixSummary> {
  const { data } = await axios.post(`/Appendix/codeindex/${encodeURIComponent(codeIndexId)}`, payload, {
    headers: { 'Content-Type': 'application/json' },
    ...(options.projectId ? { params: { projectId: options.projectId } } : {}),
  });

  return normalizeAppendixSummary(data);
}

function normalizeAppendixDetail(raw: any): AppendixDetail {
  const tables = Array.isArray(raw?.tables) ? raw.tables : [];

  return {
    id: String(raw?.id || ''),
    title: raw?.title || '',
    description: raw?.description || '',
    letter: raw?.letter || '',
    entityId: String(raw?.entityId || ''),
    ownerLabel: raw?.ownerLabel || '',
    tables: tables.map((table: any) => ({
      id: table?.id || null,
      name: table?.name || '',
      description: table?.description || '',
      columns: (Array.isArray(table?.columns) ? table.columns : []).map((column: any) => ({
        id: column?.id || null,
        name: column?.name || '',
        title: column?.title || '',
        dataType: column?.dataType || null,
        length: column?.length ?? null,
        unit: column?.unit ?? null,
        position: Number(column?.position ?? 0),
        codeItemData: column?.codeItemData ?? null,
      })),
      rows: (Array.isArray(table?.rows) ? table.rows : []).map((row: any) => ({
        id: row?.id || null,
        position: Number(row?.position ?? 0),
        cells: (Array.isArray(row?.cells) ? row.cells : []).map((cell: any) => ({
          id: cell?.id || null,
          value: cell?.value ?? null,
          notes: cell?.notes ?? null,
          columnPosition: Number(cell?.columnPosition ?? 0),
        })),
      })),
    })),
  };
}

function appendixRoute(codeIndexId: string, appendixId: string): string {
  return `/Appendix/codeindex/${encodeURIComponent(codeIndexId)}/${encodeURIComponent(appendixId)}`;
}

export async function fetchCodeIndexAppendix(
  codeIndexId: string,
  appendixId: string,
  options: { projectId?: string } = {}
): Promise<AppendixDetail> {
  const { data } = await axios.get(appendixRoute(codeIndexId, appendixId), {
    ...(options.projectId ? { params: { projectId: options.projectId } } : {}),
  });

  return normalizeAppendixDetail(data);
}

// `changeId` revises a rejected appendix change while the project is submitted for review.
export async function updateCodeIndexAppendix(
  codeIndexId: string,
  appendixId: string,
  payload: CreateAppendixPayload,
  options: { projectId?: string; changeId?: string } = {}
): Promise<AppendixSummary> {
  const params: Record<string, string> = {};
  if (options.projectId) params.projectId = options.projectId;
  if (options.changeId) params.changeId = options.changeId;

  const { data } = await axios.put(appendixRoute(codeIndexId, appendixId), payload, {
    headers: { 'Content-Type': 'application/json' },
    ...(Object.keys(params).length > 0 ? { params } : {}),
  });

  return normalizeAppendixSummary(data);
}

export async function deleteCodeIndexAppendix(
  codeIndexId: string,
  appendixId: string,
  options: { projectId?: string } = {}
): Promise<void> {
  await axios.delete(appendixRoute(codeIndexId, appendixId), {
    ...(options.projectId ? { params: { projectId: options.projectId } } : {}),
  });
}
